import { useQuery } from "@tanstack/react-query";
import { StateView } from "@ozpanel/ui";
import type { ActionDef } from "@ozpanel/kit";
import type { Tenant, TeardownPreview } from "../../accountsApi";

// tenantActions — declared ONCE and reused by both the list (row menu) and the detail page
// (action bar), so the two can never drift into offering different things for the same
// object. Danger levels mirror the backend's own declarations exactly:
//   suspend      high    unsuspend  medium
//   impersonate  high    delete     high
type Api = ReturnType<typeof import("../../accountsApi").useAccountsApi>;

export function tenantActions(
  api: Api,
  opts: {
    onDeleted?: () => void;
    onImpersonated?: () => void;
    loadTeardown: (id: number) => Promise<TeardownPreview>;
  },
): ActionDef<Tenant>[] {
  const invalidate = (t: Tenant) => [["tenants"], ["tenant", t.id]];

  return [
    {
      key: "suspend",
      label: "Suspend",
      danger: "high",
      available: (t) => t.status === "active",
      confirm: (t) => ({
        title: `Suspend ${t.name}?`,
        body: (
          <p>
            Their sites, mail and databases stop serving until you unsuspend them. Nothing is
            deleted.
          </p>
        ),
        confirmLabel: "Suspend",
      }),
      run: (t) => api.suspendTenant(t.id),
      invalidates: invalidate,
      successMessage: (t) => `${t.name} suspended`,
    },
    {
      key: "unsuspend",
      label: "Unsuspend",
      available: (t) => t.status !== "active",
      run: (t) => api.unsuspendTenant(t.id),
      invalidates: invalidate,
      successMessage: (t) => `${t.name} unsuspended`,
    },
    {
      key: "impersonate",
      label: "Sign in as",
      danger: "high",
      confirm: (t) => ({
        title: `Sign in as ${t.name}?`,
        body: (
          <p>
            Your session becomes theirs until you stop impersonating — the admin console is
            not available while acting as another account, and you return with “back” in the
            header. Everything you do is recorded against your own account in the audit trail.
          </p>
        ),
        confirmLabel: "Sign in as this account",
      }),
      run: (t) => api.impersonateTenant(t.id),
      successMessage: (t) => `Now acting as ${t.name} — use “back” in the header to return`,
      // The session changed SERVER-SIDE. Without re-reading it the console keeps rendering
      // the previous principal while every request is made as someone else — which is how
      // this turned into a lockout the first time it shipped.
      onDone: () => opts.onImpersonated?.(),
    },
    {
      key: "delete",
      label: "Delete",
      danger: "high",
      // The confirmation body is the SERVER'S OWN teardown preview, fetched when the dialog
      // opens — GET /tenants/{id}/teardown exists precisely to answer "what would this
      // remove". Confirming a destructive act without showing what it destroys is the
      // Interlock failure this codebase already documents elsewhere.
      confirm: (t) => ({
        title: `Delete ${t.name}?`,
        body: <TeardownBody tenantId={t.id} load={opts.loadTeardown} />,
        confirmLabel: "Delete permanently",
      }),
      run: (t) => api.deleteTenant(t.id),
      invalidates: () => [["tenants"]],
      successMessage: (t) => `${t.name} deleted`,
      onDone: () => opts.onDeleted?.(),
    },
  ];
}

// TeardownReport renders whatever keys the endpoint returns, without hardcoding them: the
// server owns this vocabulary and has already changed it once. Keys become headings, string
// lists become bullets, and anything unrecognised still gets shown rather than dropped —
// silently hiding a field from a DELETE confirmation is the worst possible failure here.
const KEY_TONE: Record<string, string> = {
  not_covered: "text-amber-700 dark:text-amber-300",
  keeps_without_purge: "text-neutral-700 dark:text-neutral-300",
};

function TeardownReport({ data }: { data: TeardownPreview }) {
  const entries = Object.entries(data).filter(([, v]) => v !== null && v !== undefined && v !== "");
  if (entries.length === 0) {
    return <p className="text-neutral-500">The server reported nothing further.</p>;
  }
  return (
    <div className="space-y-3">
      {entries.map(([key, value]) => (
        <div key={key}>
          <h4 className="mb-1 text-xs font-semibold uppercase tracking-wide text-neutral-500">
            {key.replace(/_/g, " ")}
          </h4>
          {Array.isArray(value) ? (
            <ul className={`list-disc space-y-0.5 pl-5 ${KEY_TONE[key] ?? ""}`}>
              {value.map((v, i) => (
                <li key={i}>{String(v)}</li>
              ))}
            </ul>
          ) : typeof value === "string" ? (
            <p className={`whitespace-pre-wrap ${KEY_TONE[key] ?? ""}`}>{value}</p>
          ) : (
            <pre className="overflow-auto rounded bg-neutral-100 p-2 text-xs dark:bg-neutral-800">
              {JSON.stringify(value, null, 2)}
            </pre>
          )}
        </div>
      ))}
    </div>
  );
}

function TeardownBody({ tenantId, load }: { tenantId: number; load: (id: number) => Promise<TeardownPreview> }) {
  const preview = useQuery({ queryKey: ["tenant-teardown", tenantId], queryFn: () => load(tenantId) });
  return (
    <div>
      <p className="mb-2 font-medium text-red-700 dark:text-red-300">
        This cannot be undone. The server reports this would remove:
      </p>
      <StateView
        loading={preview.isLoading}
        error={preview.error}
        errorContext="the teardown preview"
        loadingMessage="Asking the server what this would remove…"
      >
        <TeardownReport data={preview.data ?? {}} />
      </StateView>
    </div>
  );
}
